import { useCallback, useEffect, useRef, useState } from 'react'
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react'
import { useDiagramStore, useHistoryStore, useSelectionStore, useViewportStore } from '@/stores'
import { useDraftStore } from '@/stores/draftStore'
import { useTabViewStore } from '@/stores/tabViewStore'
import { getTableBounds } from '@/utils/geometry'
import { shouldFlipAnchorSide, flipAnchor } from '@/utils/paths'

interface DragTarget {
  id: string
  x: number
  y: number
}

interface DragState {
  pointerId: number
  startClientX: number
  startClientY: number
  tableId: string
  targets: DragTarget[]
}

function sanitizeCoordinate(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function buildHistoryLabel(tableId: string, movedCount: number): string {
  return movedCount > 1 ? `move-tables:${movedCount}` : `move-table:${tableId}`
}

export function useTableDrag(tableId: string) {
  const dragStateRef = useRef<DragState | null>(null)
  const headerElementRef = useRef<HTMLElement | null>(null)
  const movedRef = useRef(false)
  const suppressClickRef = useRef(false)
  const restoreBodyUserSelectRef = useRef<string>('')
  const restoreBodyCursorRef = useRef<string>('')
  const [isDragging, setIsDragging] = useState(false)

  const cleanup = useCallback((shouldRestoreOriginalPosition: boolean) => {
    const dragState = dragStateRef.current
    const headerElement = headerElementRef.current

    if (headerElement && dragState) {
      try {
        if (headerElement.hasPointerCapture(dragState.pointerId)) {
          headerElement.releasePointerCapture(dragState.pointerId)
        }
      } catch {
        // no-op: pointer may already be gone
      }
    }

    window.removeEventListener('pointermove', handlePointerMove)
    window.removeEventListener('pointerup', handlePointerUp)
    window.removeEventListener('pointercancel', handlePointerCancel)

    if (shouldRestoreOriginalPosition && dragState) {
      const originalById = new Map(
        dragState.targets.map((target) => [target.id, target] as const),
      )

      useDiagramStore.setState((state) => ({
        tables: state.tables.map((table) => {
          const original = originalById.get(table.id)
          if (!original) return table
          return { ...table, x: original.x, y: original.y }
        }),
      }))
    }

    document.body.style.userSelect = restoreBodyUserSelectRef.current
    document.body.style.cursor = restoreBodyCursorRef.current

    dragStateRef.current = null
    headerElementRef.current = null
    movedRef.current = false
    setIsDragging(false)
  }, [])

  const handlePointerMove = useCallback((event: PointerEvent) => {
    const dragState = dragStateRef.current
    if (!dragState || event.pointerId !== dragState.pointerId) return

    event.preventDefault()

    const sc = useViewportStore.getState().sc
    if (!Number.isFinite(sc) || sc === 0) return

    const dx = (event.clientX - dragState.startClientX) / sc
    const dy = (event.clientY - dragState.startClientY) / sc
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) return

    if (!movedRef.current && (Math.abs(dx) > 0 || Math.abs(dy) > 0)) {
      movedRef.current = true
      setIsDragging(true)
    }

    const nextById = new Map(
      dragState.targets.map((target) => [
        target.id,
        {
          x: sanitizeCoordinate(target.x + dx),
          y: sanitizeCoordinate(target.y + dy),
        },
      ]),
    )

    useDiagramStore.setState((state) => ({
      tables: state.tables.map((table) => {
        const next = nextById.get(table.id)
        if (!next) return table
        return { ...table, x: next.x, y: next.y }
      }),
    }))
  }, [])

  const handlePointerUp = useCallback((event: PointerEvent) => {
    const dragState = dragStateRef.current
    if (!dragState || event.pointerId !== dragState.pointerId) return

    const didMove = movedRef.current
    const tableId = dragState.tableId
    const targets = dragState.targets
    cleanup(false)
    suppressClickRef.current = didMove

    if (!didMove) return

    // After move: flip anchors for relations connected to moved tables
    const state = useDiagramStore.getState()
    const movedIds = new Set(targets.map((t) => t.id))
    const tableById = new Map(state.tables.map((t) => [t.id, t]))

    let anyFlipped = false
    const updatedRels = state.rels.map((rel) => {
      if (!movedIds.has(rel.from) && !movedIds.has(rel.to)) return rel
      const ft = tableById.get(rel.from)
      const tt = tableById.get(rel.to)
      if (!ft || !tt) return rel

      const fromRect = getTableBounds(ft)
      const toRect = getTableBounds(tt)
      let updated = rel

      if (rel.fromAnchor?.side && shouldFlipAnchorSide(rel.fromAnchor, fromRect, toRect)) {
        updated = { ...updated, fromAnchor: flipAnchor(rel.fromAnchor) }
        anyFlipped = true
      }
      if (rel.toAnchor?.side && shouldFlipAnchorSide(rel.toAnchor, toRect, fromRect)) {
        updated = { ...updated, toAnchor: flipAnchor(rel.toAnchor) }
        anyFlipped = true
      }
      return updated
    })

    if (anyFlipped) {
      useDiagramStore.setState({ rels: updatedRels })
    }

    const { tables, groups, defaults, projectName, dialect } = useDiagramStore.getState()
    const hiddenTableIds = useTabViewStore.getState().hiddenTableIds
    useHistoryStore.getState().commitHistory(
      { tables, rels: anyFlipped ? updatedRels : state.rels, groups, defaults, projectName, dialect, hiddenTableIds },
      buildHistoryLabel(tableId, targets.length),
    )
  }, [cleanup])

  const handlePointerCancel = useCallback((event: PointerEvent) => {
    const dragState = dragStateRef.current
    if (!dragState || event.pointerId !== dragState.pointerId) return
    
    const didMove = movedRef.current
    cleanup(true)
    suppressClickRef.current = didMove
  }, [cleanup])

  useEffect(() => () => cleanup(true), [cleanup])

  const handleHeaderPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return
    if (useDraftStore.getState().mode === 'view') return

    const target = event.target as HTMLElement
    if (target.closest('[data-action]')) return
    if (useSelectionStore.getState().relMode) return

    event.preventDefault()
    event.stopPropagation()

    const selectionState = useSelectionStore.getState()
    const selectedTableIds = selectionState.selectedTableIds
    const isPartOfMultiSelection =
      selectedTableIds.size > 1 && selectedTableIds.has(tableId)

    if (!selectedTableIds.has(tableId) || !isPartOfMultiSelection) {
      selectionState.selectOnlyTable(tableId)
    }

    const targetIds = isPartOfMultiSelection
      ? new Set(selectedTableIds)
      : new Set([tableId])

    const targets = useDiagramStore
      .getState()
      .tables
      .filter((table) => targetIds.has(table.id))
      .map((table) => ({
        id: table.id,
        x: sanitizeCoordinate(table.x),
        y: sanitizeCoordinate(table.y),
      }))

    if (!targets.length) return

    restoreBodyUserSelectRef.current = document.body.style.userSelect
    restoreBodyCursorRef.current = document.body.style.cursor
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'grabbing'

    dragStateRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      tableId,
      targets,
    }
    headerElementRef.current = event.currentTarget
    movedRef.current = false
    setIsDragging(false)

    event.currentTarget.setPointerCapture(event.pointerId)
    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerCancel)
  }, [handlePointerCancel, handlePointerMove, handlePointerUp, tableId])

  const handleTableClickCapture = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    if (!suppressClickRef.current) return

    suppressClickRef.current = false
    event.preventDefault()
    event.stopPropagation()
  }, [])

  return {
    isDragging,
    handleHeaderPointerDown,
    handleTableClickCapture,
  }
}
